import { ThemeProvider, EmotionCacheProvider } from '@ori-ui/mui';
import { doDevelopmentLogin, NOT_LOGGED_IN_USER_ID, UniversalTokenProvider } from '@ori/auth';
import { parseClientAppConfig } from '@ori/client-app-config';
import { ServiceUrlsProvider } from '@ori/configuration-manager';
import { ClientCultureProvider } from '@ori/culture';
import { EnvironmentProvider, getEnvironmentFromHost } from '@ori/environment';
import { defaultTranslationsWithNamespaces, FooterApp, FooterAppSkeleton, FooterErrorBoundary } from '@ori/footer';
import { TranslationsNamespaceProvider, TranslationsProvider } from '@ori/i18n';
import { translationsNamespace as languageSwitcherTranslationsNamespace } from '@ori/language-switcher';
import { LanguageSwitcherPopup, useLanguageSwitcherTranslations } from '@ori/language-switcher';
import { ApplicationInsights, getClientApplicationInsightsConfig, LoggerProvider } from '@ori/logger';
import { ApiClientProvider as StaticApiClientProvider } from '@ori/static-api-client-react';
import { Suspense, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { EMOTION_CACHE_KEY } from './constants-generated';
import { mainLogger } from './logger';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const LocalizedLanguageSwitcher = props => {
  const {
    translations
  } = useLanguageSwitcherTranslations();
  return /*#__PURE__*/_jsx(LanguageSwitcherPopup, {
    ...props,
    translations: useMemo(() => ({
      selectLanguage: translations.selectLanguage
    }), [translations.selectLanguage])
  });
};
const logger = mainLogger.createChild('appRender');

/**
 * React app renderer that is used when app is used as a standalone app
 */
export async function render(rootElement) {
  const {
    config = ''
  } = rootElement.dataset;
  const clientAppConfig = parseClientAppConfig(config, ['currentLanguage', 'globalApiUrl', 'staticApiUrl', 'tenant']);
  const {
    currentLanguage,
    customerId = NOT_LOGGED_IN_USER_ID,
    disableLanguageSwitcherPopUp,
    globalApiUrl,
    identityUrl,
    isFrontPage,
    isStandalone = false,
    showFooterOnDesktop = true,
    staticApiUrl,
    tenant
  } = clientAppConfig;
  const appProps = {
    configuration: {
      isFrontPage,
      isStandalone,
      showFooterOnDesktop
    }
  };
  const localizedLanguageSwitcherProps = {
    currentLanguage,
    staticApiUrl,
    tenant
  };
  if (!showFooterOnDesktop) {
    return;
  }

  // Get token for local development
  if (process.env.NODE_ENV === 'production') {
    ApplicationInsights.initialize(getClientApplicationInsightsConfig());
  } else {
    // when using Requestly we do not want to load token
    // eslint-disable-next-line no-lonely-if -- thats okay
    if (process.env.REACT_APP_USE_REQUESTLY === '1') {
      logger.debug('USING REQUESTLY');
    } else {
      if (!identityUrl) {
        throw new Error('Missing Identity URL!');
      }
      await doDevelopmentLogin({
        identityUrl,
        tenant
      });
    }
  }
  const environment = getEnvironmentFromHost(window.location.host);
  createRoot(rootElement).render(/*#__PURE__*/_jsx(LoggerProvider, {
    logger: mainLogger,
    children: /*#__PURE__*/_jsx(FooterErrorBoundary, {
      children: /*#__PURE__*/_jsx(ServiceUrlsProvider, {
        serviceUrls: {
          globalApiUrl,
          staticApiUrl,
          identityUrl
        },
        children: /*#__PURE__*/_jsx(EnvironmentProvider, {
          tenant: tenant,
          environment: environment,
          children: /*#__PURE__*/_jsx(UniversalTokenProvider, {
            customerId: customerId,
            initialToken: "",
            children: /*#__PURE__*/_jsx(EmotionCacheProvider, {
              cacheKey: EMOTION_CACHE_KEY,
              children: /*#__PURE__*/_jsx(ThemeProvider, {
                children: /*#__PURE__*/_jsx(StaticApiClientProvider, {
                  baseUrl: staticApiUrl,
                  language: currentLanguage,
                  tenant: tenant,
                  children: /*#__PURE__*/_jsx(ClientCultureProvider, {
                    tenant: tenant,
                    children: /*#__PURE__*/_jsx(Suspense, {
                      fallback: /*#__PURE__*/_jsx(FooterAppSkeleton, {}),
                      children: /*#__PURE__*/_jsxs(TranslationsProvider, {
                        defaultTranslationsWithNamespaces: defaultTranslationsWithNamespaces,
                        clientFetchConfiguration: {
                          baseUrl: staticApiUrl,
                          tenant,
                          // eslint-disable-next-line no-console -- we need to log errors
                          onError: console.error
                        },
                        language: currentLanguage,
                        children: [!disableLanguageSwitcherPopUp && /*#__PURE__*/_jsx(TranslationsNamespaceProvider, {
                          namespace: languageSwitcherTranslationsNamespace,
                          children: /*#__PURE__*/_jsx(LocalizedLanguageSwitcher, {
                            ...localizedLanguageSwitcherProps
                          })
                        }), /*#__PURE__*/_jsx(FooterApp, {
                          ...appProps
                        })]
                      })
                    })
                  })
                })
              })
            })
          })
        })
      })
    })
  }));
}